<template>
  <v-container fluid>
    <v-card>
      <v-toolbar
        flat
        color="white"
      >
        <v-spacer />
        <duty-roster-dialog-create
          v-if="$hasPermissionTo(['bulk create duty rosters'])"
          class="ma-2"
          @created="loadDutyRosters"
        />
        <duty-roster-dialog-import
          v-if="$hasPermissionTo(['import duty rosters'])"
          class="ma-2"
          @imported="loadDutyRosters"
        />
      </v-toolbar>
      
      <v-btn
        fab
        text
        small
        color="grey darken-2"
        @click="prev"
      >
        <v-icon small>
          mdi-chevron-left
        </v-icon>
      </v-btn>
      <span> {{ calendarTitle }} </span>
      <v-btn
        fab
        text
        small
        color="grey darken-2"
        @click="next"
      >
        <v-icon small>
          mdi-chevron-right
        </v-icon>
      </v-btn>
      <v-sheet
        max-height="400" 
        width="100%"
        border-sm
        elevation="2"
        style="border-top: 2px"
      >
        <v-row 
          v-if="calendarLoading"
          no-gutters
        >
          <v-progress-linear
            indeterminate
            color="primary"
          />
        </v-row>
        <v-row 
          v-else
          no-gutters
        >
          <v-calendar
            v-if="calendarReady"
            ref="calendar"
            v-model="today"
            color="primary"
            :events="events"
            :event-color="getEventColor"
            type="month"
            @click:date="onClickDate"
            @change="updateTitle"
          >
            <template v-slot:event="{ event }">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }"> 
                  <div 
                    class="pl-1 py-0 font-calibri"
                    v-bind="attrs"
                    v-on="on"
                    @click.stop="openEditForm(event)"
                  >
                    <span>{{ event.shiftTime + ' : ' + event.name }}</span>
                  </div>
                </template>
                <div>{{ event.name }}</div>
                <div>
                  {{ event.shiftTime }}
                  <span 
                    v-if="event.shiftCode"
                    class="font-weight-bold"
                  >
                    {{ ' (' + event.shiftCode + ')' }}
                  </span>
                </div>
                <span 
                  v-if="event.remark"
                  v-html="'<bold>Remark:</bold> ' + event.remark.replace(/\n/g, '</br>')" 
                />
              </v-tooltip>
            </template>
          </v-calendar>
        </v-row>
      </v-sheet>
    </v-card>
    
    <v-dialog
      v-if="selectedEvent"
      v-model="showEventDialog"
      persistent
      max-width="500px"
    >
      <material-card
        :offset="10"
        :full-width="true"
        :loading="loading"
        color="hrissecondary"
        :title="selectedEvent.name + ' (' + selectedEvent.start + ')'"
        :text="'Edit / Delete ' + selectedEvent.name + '\'s Duty Roster Record for ' + selectedEvent.start"
        class="mb-0"
      >
        <v-row 
          v-if="!loading"
          no-gutters
          class="mt-5"
        >
          <v-col 
            cols="12"
            class="py-0"
          >
            <v-switch
              v-model="useShift"
              label="Use shift from work session type"
              :disabled="!$hasPermissionTo(['update duty rosters'])"
              @change="selectedShift = null"
            />
          </v-col>
          <v-col
            v-if="useShift"
            cols="12"
            class="py-0"
          >
            <v-autocomplete 
              v-model="selectedShift"
              v-validate="useShift ? 'required' : ''"
              :error-messages="errors.collect('selectedShift')"    
              data-vv-name="selectedShift"
              data-vv-as="shift"
              placeholder="Select shift"
              :items="workSessionShiftCodes"
              item-text="code"
              item-value="id"
              return-object
              label="Shift Code"
              hide-selected
              autofocus
              outlined
              class="label-position"
              :disabled="!$hasPermissionTo(['update duty rosters'])"
              @change="setValues()"
            />
          </v-col>
          <v-col 
            cols="6"
            class="py-0 pr-2"
          >
            <v-text-field
              v-model="localEvent.start_time"
              v-validate="'required|time_before_or_equal:' + localEvent.end_time"
              :error-messages="errors.collect('startTime')"    
              data-vv-name="startTime"
              data-vv-as="start time"
              :readonly="useShift"
              outlined
              label="Start Time"
              type="time"
              class="label-position"
              :disabled="useShift || !$hasPermissionTo(['update duty rosters'])"
            />
          </v-col>
          <v-col 
            cols="6"
            class="py-0 pl-2"
          >
            <v-text-field
              v-model="localEvent.end_time"
              v-validate="'required|time_after_or_equal:' + localEvent.start_time"
              :error-messages="errors.collect('endTime')"    
              data-vv-name="endTime"
              data-vv-as="end time"
              :readonly="useShift"
              outlined
              label="End Time"
              type="time"
              class="label-position"
              :disabled="useShift || !$hasPermissionTo(['update duty rosters'])"
            />
          </v-col>
          <v-col
            cols="12"
          >
            <v-textarea
              v-model="localEvent.remark"
              :rows="2"
              outlined
              label="Remark"
              class="label-position"
              :disabled="!$hasPermissionTo(['update duty rosters'])"
            />
          </v-col>
        </v-row>
    
        <v-dialog
          v-model="deleteConfirmationDialog"
          max-width="400px"
        >
          <v-card>
            <v-card-title class="headline">
              Delete Confirmation
            </v-card-title>
            <v-card-text>
              Are you sure to delete this duty roster?
            </v-card-text>

            <v-card-actions>
              <v-spacer />
              <v-btn
                :disabled="deleteEventProgressing"
                text
                @click="deleteConfirmationDialog = false"
              >
                No
              </v-btn>
              <v-btn
                :loading="deleteEventProgressing"
                color="hrissecondary"
                class="white--text"
                @click="deleteEventForm"
              >
                Yes
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-card-actions v-if="showEventDialog && !loading">
          <v-spacer />
          <v-btn
            :disabled="editEventProgressing || deleteEventProgressing"
            text
            class="px-5"
            @click="showEventDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            v-if="$hasPermissionTo(['update duty rosters'])"
            :loading="editEventProgressing"
            :disabled="deleteEventProgressing"
            color="hrissecondary"    
            class="white--text px-5"      
            @click="editEventForm"
          >
            Update
          </v-btn>
          <v-btn
            v-if="$hasPermissionTo(['delete duty rosters'])"
            :loading="deleteEventProgressing"
            :disabled="editEventProgressing"
            color="error"    
            class="white--text px-5"      
            @click="deleteConfirmationDialog = true"
          >
            Delete
          </v-btn>
        </v-card-actions>
      </material-card>
    </v-dialog>
  </v-container>
</template>

<script>
  export default {
    $_veeValidate: {
      validator: 'new', 
    },
    components: {
      DutyRosterDialogCreate: () => import('@/components/dutyRoster/DialogCreate'),
      DutyRosterDialogImport: () => import('@/components/dutyRoster/DialogImport'),
    },
    data () {
      return {
        loading: false,
        today: new Date().toISOString().slice(0, 10),
        showCalendar: false,
        showForm: false,
        selectedDate: null,
        progressing: false,
        events: [],
        currentMonth: this.$moment().format('YYYY-MM'),
        calendarTitle: '',
        startTime: '00:00:00',
        endTime: '00:00:00',
        remark: null,
        calendarLoading: false,
        showEventDialog: false,
        selectedEvent: null,
        localEvent: null,
        editEventProgressing: false,
        deleteEventProgressing: false,
        deleteConfirmationDialog: false,
        calendarReady: false,
        useShift: true,
        workSessionShiftCodes: [],
        selectedShift: null,
      }
    },
    computed: {
      eventDates () {
        return this.events.map(e => this.$moment(e.start).format('YYYY-MM-DD'))
      },
    },
    watch: {
      showCalendar (val) {
        if (val) {
          this.$nextTick(() => {
            this.updateTitle()
          })
        }
      },
    },
    mounted () {
      this.loadDutyRosters()

      function toMinutes (time) {
        if (!time) return null
        const [h, m] = time.split(':')
        return (parseInt(h) * 60) + parseInt(m)
      }

      this.$validator.extend('time_before_or_equal', {
        getMessage (field, args) {
          return `The ${field} must be earlier than end time.`
        },
        validate (value, args) {
          const [otherValue] = args
          return toMinutes(value) <= toMinutes(otherValue)
        },
      })

      this.$validator.extend('time_after_or_equal', {
        getMessage (field, args) {
          return `The ${field} must be later than start time.`
        },
        validate (value, args) {
          const [otherValue] = args
          return toMinutes(value) >= toMinutes(otherValue)
        },
      })
    },
    methods: {
      updateTitle () {
        if (this.$refs.calendar && this.$refs.calendar.value) {
          const newMonth = this.$moment(this.$refs.calendar.value).format('YYYY-MM')
          if (!this.calendarTitle || newMonth !== this.currentMonth) {
            this.currentMonth = newMonth
            this.calendarTitle = this.$moment(this.$refs.calendar.value).format('MMM YYYY')
            this.loadDutyRosters()
          }
        } else {
          this.calendarTitle = this.$moment().format('MMM YYYY')
        }
      },
      prev () {
        this.$refs.calendar.prev()
      },
      next () {
        this.$refs.calendar.next()
      },
      loadDutyRosters () {
        this.calendarLoading = true
        const date = this.$refs.calendar && this.$refs.calendar.value ? this.$refs.calendar.value : this.today

        this.$api.dutyRosters().listAll('?date=' + date)
          .then(res => {
            this.events = _.orderBy(res.data.map(roster => {
              return {
                id: roster.id,
                name: roster.employee.person.name,
                start: roster.date,
                startTime: roster.start_time,
                endTime: roster.end_time,
                employeeUid: roster.employee_uid,
                shiftCodeId: roster.work_session_shift_code_id,
                shiftCode: roster.work_session_shift_code ? roster.work_session_shift_code.code : null,
                remark: roster.remark,
                color: roster.start_time === roster.end_time ? 'red' : 'blue',
                shiftTime: (roster.start_time === roster.end_time ? 'OFF DAY' : this.$moment(roster.start_time, 'hh:mm').format('hh:mm A') + ' - ' + this.$moment(roster.end_time, 'hh:mm').format('hh:mm A')),
              }
            }), ['start'], ['asc'])

            this.calendarLoading = false

            if (!this.calendarReady) {
              this.$nextTick(() => {
                this.updateTitle()
                this.calendarReady = true
              })
            }
          })
      },
      setValues () {
        this.localEvent.work_session_shift_code_id = this.selectedShift.id
        this.localEvent.start_time = this.selectedShift.start_time
        this.localEvent.end_time = this.selectedShift.end_time
      },
      onClickDate (day) {
        const date = this.$moment(day.date).format('YYYY-MM-DD')

        if (this.$moment(date).isBefore(this.$moment(), 'day')) {
          return
        }

        if (this.eventDates.includes(date)) {
          return
        }

        this.selectedDate = this.$moment(day.date).format('YYYY-MM-DD')
        
        this.showForm = true
      },
      submitForm () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return

            this.progressing = true

            this.$api.dutyRosters().create(this.prepareFormData())
              .then(res => {
                this.loadDutyRosters()
                this.$snack({ message: res.data.status })
                this.startTime = '00:00:00'
                this.endTime = '00:00:00'
                this.remark = null
                this.progressing = false
                this.showForm = false
              })
          })
      },
      prepareFormData () {
        return {
          start_time: this.startTime,
          end_time: this.endTime,
          is_working_day: this.startTime === this.endTime ? 0 : 1,
          date: this.selectedDate,
          remark: this.remark,
        }
      },
      editEventForm () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return

            this.editEventProgressing = true
            this.localEvent.work_session_shift_code_id = this.useShift ? this.selectedShift.id : null
            this.localEvent.is_working_day = this.localEvent.start_time === this.localEvent.end_time ? 0 : 1

            this.$api.dutyRosters().update(this.selectedEvent.id, this.localEvent)
              .then(res => {
                this.loadDutyRosters()
                this.$snack({ message: res.data.status })
                
                this.editEventProgressing = false
                this.showEventDialog = false
              })
          })
      },
      deleteEventForm () {
        this.deleteEventProgressing = true

        this.$api.dutyRosters().delete(this.selectedEvent.id)
          .then(res => {
            this.loadDutyRosters()
            this.$snack({ message: res.data.status })
            
            this.deleteEventProgressing = false
            this.showEventDialog = false
          })
      },
      loadEmployeeWorkdayShiftCodes () {
        this.loading = true

        this.$api.employeeShiftCodes().list(this.selectedEvent.employeeUid)
          .then(res => {
            this.workSessionShiftCodes = res.data
            this.loading = false
          })
      },
      getEventColor (event) {
        return event.color
      },
      openEditForm (event) {
        if (this.$hasPermissionTo(['update duty rosters']) || this.$hasPermissionTo(['delete duty rosters'])) {
          this.selectedEvent = event

          this.localEvent = {
            work_session_shift_code_id: event.shiftCodeId,
            start_time: event.startTime,
            end_time: event.endTime,
            remark: event.remark,
          }

          if (event.shiftCodeId) {
            this.useShift = true
            this.selectedShift = event.shiftCodeId
          } else {
            this.useShift = false
            this.selectedShift = null
          }

          this.loadEmployeeWorkdayShiftCodes()
          this.showEventDialog = true
        }
      },
    },
  }
</script>

<style scoped>
.label-position >>> .v-label.v-label--active{
  margin-left:-5px;
  padding: 0 2px;
  background: white;
}
</style>
